import { useTranslation } from "react-i18next";
import CommonStyles from "@components/CommonStyles";
import { ISpecialization } from "@base/modules/specialization/interface/specialization.interface";
import useGetSpecializetation from "@base/modules/specialization/hooks/useGetSpecialization";
import {
  extraFieldKind,
  extraFieldLabel,
  ExtraFieldValue,
  IExtraAddressValue,
  IExtraFieldConfig,
  isExtraFieldRequired,
} from "@base/modules/patientSpecialPackage/packageBookingRules";
import ServiceAddressFields from "./ServiceAddressFields";

interface IProps {
  fields: IExtraFieldConfig[];
  values: Record<string, ExtraFieldValue>;
  errors: Record<string, string>;
  onChange: (fieldName: string, value: ExtraFieldValue) => void;
}

/**
 * The package's "Additional information" (§12.10). Which inputs exist, their labels and whether they
 * are required is configuration; the kind of input follows the field's NAME.
 */
export default function ExtraFields({ fields, values, errors, onChange }: IProps) {
  //! state
  const { t, i18n } = useTranslation();
  const { data: dataSpecialization } = useGetSpecializetation({ lang: i18n.resolvedLanguage });
  // "ALL" and nameless entries are filters, not specialties.
  const specializations = (dataSpecialization?.data ?? []).filter(
    (item: ISpecialization) => !!item.specialization_code?.trim() && item.specialization_code.trim().toUpperCase() !== "ALL",
  );

  //! render
  if (!fields.length) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="text-common-maintext text-base font-medium font-['Inter']">{t("packageBooking.extraInformation")}</div>

      {fields.map((field) => {
        const fieldName = String(field.field_name);
        const label = extraFieldLabel(field, i18n.resolvedLanguage);
        const isRequired = isExtraFieldRequired(field);
        const error = errors[fieldName];
        const kind = extraFieldKind(field);

        if (kind === "ADDRESS") {
          return (
            <ServiceAddressFields
              key={fieldName}
              label={label}
              required={isRequired}
              // Province and ward only: there is no street line in this field.
              withStreet={false}
              error={error}
              value={(values[fieldName] as IExtraAddressValue) ?? {}}
              onChange={(value) => onChange(fieldName, value)}
            />
          );
        }

        if (kind === "SPECIALIZATION") {
          const selected = values[fieldName] as ISpecialization | undefined;

          return (
            <div key={fieldName} className="flex flex-col gap-2">
              <span className="text-common-maintext text-sm font-medium font-['Inter']">
                {label} {isRequired ? <span className="text-common-red500">*</span> : null}
              </span>

              <CommonStyles.SelectUI
                variant="filled"
                showSearch
                allowClear
                optionFilterProp="label"
                placeholder={t("booking.selectPlaceholder")}
                value={selected?.specialization_code}
                options={specializations.map((specialization: ISpecialization) => ({
                  label: specialization.description || specialization.specialization_name,
                  value: specialization.specialization_code,
                }))}
                onChange={(value: string) => onChange(fieldName, specializations.find((item: ISpecialization) => item.specialization_code === value))}
              />

              {error ? <span className="text-common-red500 text-xs font-normal font-['Inter']">{error}</span> : null}
            </div>
          );
        }

        return (
          <div key={fieldName} className="flex flex-col gap-2">
            <span className="text-common-maintext text-sm font-medium font-['Inter']">
              {label} {isRequired ? <span className="text-common-red500">*</span> : null}
            </span>

            <CommonStyles.InputUI
              variant="filled"
              maxLength={254}
              value={(values[fieldName] as string) ?? ""}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(fieldName, event.target.value)}
            />

            {error ? <span className="text-common-red500 text-xs font-normal font-['Inter']">{error}</span> : null}
          </div>
        );
      })}
    </div>
  );
}
